import React from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes } from 'styled-components';
import { GoogleLogin } from '@react-oauth/google';
import colors from 'theme/colors';
import fonts from 'theme/fonts';

/**
 * Botões de login social — Google e Apple com a mesma casca.
 *
 * O componente não tem visual próprio: ele veste a linguagem da tela que o
 * hospeda, escolhida por `shape`. Foi a lição da primeira versão, que impunha um
 * estilo só e deixava os dois botões destoando dos irmãos ao lado deles.
 *
 * O botão da Google **não** é desenhado por nós de verdade: o widget oficial é
 * renderizado por baixo (`GoogleSlot`) e nós pintamos a cara por cima (`Face`).
 * Isso existe por um motivo concreto: só o widget oficial devolve o `credential`
 * (ID token JWT) que o `loginApi.withGoogle` espera. Um botão próprio chamando
 * `useGoogleLogin` traz `access_token`, não `credential`, e exigiria mexer no
 * contrato da API.
 *
 * Consequência disso: a `Face` é `pointer-events: none` para o clique atravessar
 * até o iframe. Como o foco do teclado vai parar no iframe e não num elemento
 * nosso, o anel de foco é desenhado via `:focus-within` no container — sem isso
 * quem navega por teclado não enxerga onde está.
 */

const GOOGLE_IFRAME_WIDTH = 400;

/*
 * Altura explícita, e não vinda do padding como no MethodButton que o modo
 * `soft` imita. O motivo é o botão do Google: ele nasce com 40px fixos e a única
 * forma de esticá-lo até cobrir a nossa casca é dar a ele uma altura em px. Uma
 * casca de altura automática não tem esse número para passar adiante.
 *
 * 62px é a altura que o MethodButton tem hoje (14px de padding + 34px de linha);
 * 48px é a do Button do app em `size large`. Se a tipografia de uma das telas
 * mudar, o número correspondente aqui precisa acompanhar.
 */
const HEIGHT = { soft: 62, pill: 48 };

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

export default function SocialAuthButtons({
    onGoogleSuccess,
    onGoogleError,
    onAppleClick,
    appleReady,
    shape,
    googleLabel,
    appleLabel
}) {
    return (
        <Group shape={shape}>
            <GoogleContainer shape={shape}>
                <Face shape={shape} aria-hidden="true">
                    <IconSlot shape={shape}>
                        <svg viewBox="0 0 24 24" width="20" height="20" focusable="false">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                    </IconSlot>
                    <span>{googleLabel}</span>
                </Face>

                <GoogleSlot shape={shape}>
                    <GoogleLogin
                        type="standard"
                        theme="outline"
                        shape="rectangular"
                        size="large"
                        width={GOOGLE_IFRAME_WIDTH}
                        onSuccess={onGoogleSuccess}
                        onError={onGoogleError}
                    />
                </GoogleSlot>
            </GoogleContainer>

            <AppleButton
                type="button"
                onClick={onAppleClick}
                disabled={!appleReady}
                aria-busy={!appleReady}
                shape={shape}
            >
                <IconSlot shape={shape}>
                    {appleReady ? (
                        <svg viewBox="0 0 384 512" width="18" height="18" aria-hidden="true" focusable="false">
                            <path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                        </svg>
                    ) : (
                        <Spinner aria-hidden="true" />
                    )}
                </IconSlot>
                <span>{appleReady ? appleLabel : 'Preparando…'}</span>
            </AppleButton>
        </Group>
    );
}

SocialAuthButtons.propTypes = {
    onGoogleSuccess: PropTypes.func.isRequired,
    onGoogleError: PropTypes.func.isRequired,
    onAppleClick: PropTypes.func.isRequired,
    appleReady: PropTypes.bool,
    /**
     * Qual linguagem visual vestir:
     *
     * `soft` — a do LoginModal, copiada do `MethodButton` que fica logo abaixo
     *   destes botões: radius 10px, borda 1.5px, 15px/500, ícone num slot de
     *   32×32. O slot é o que faz os três rótulos da lista começarem na mesma
     *   coluna.
     *
     * `pill` — a do `Button` do app (`variant secondary` + `category rounded` +
     *   `size large`), usada na página /entrar: radius 50px, sombra em vez de
     *   contorno, conteúdo centralizado, 16px/600.
     */
    shape: PropTypes.oneOf(['pill', 'soft']),
    googleLabel: PropTypes.string,
    appleLabel: PropTypes.string
};

SocialAuthButtons.defaultProps = {
    appleReady: false,
    shape: 'pill',
    googleLabel: 'Continuar com o Google',
    appleLabel: 'Continuar com a Apple'
};

const isSoft = ({ shape }) => shape === 'soft';

const focusRing = css`
    outline: 2px solid ${colors.secondaryPurple};
    outline-offset: 2px;
`;

/*
 * Os valores de `soft` são cópia literal do MethodButton do LoginModal, e os de
 * `pill` do StyledButton do app. Copiados de propósito em vez de importados: são
 * duas linguagens diferentes, e um token compartilhado aqui acabaria arrastando
 * uma tela quando a outra mudasse.
 */
const surface = css`
    display: flex;
    align-items: center;
    width: 100%;
    background: ${colors.neutralLight};
    color: ${colors.nightDark};
    transition: all 0.2s ease;

    ${(props) => (isSoft(props) ? css`
        gap: 14px;
        height: ${HEIGHT.soft}px;
        padding: 0 20px;
        border: 1.5px solid ${colors.neutralDark};
        border-radius: 10px;
        font-size: 15px;
        font-weight: 500;
    ` : css`
        justify-content: center;
        gap: 12px;
        height: ${HEIGHT.pill}px;
        padding: 0 20px;
        border: 1px solid ${colors.neutralLight};
        border-radius: 50px;
        box-shadow: 0 2px 3px ${colors.nightLight};
        font-size: ${fonts.sizes.highlight};
        font-weight: ${fonts.weight.bold};
    `)}
`;

const hovered = css`
    ${(props) => (isSoft(props) ? css`
        border-color: ${colors.occasionalPurple}40;
        background: #FAFAFF;
    ` : css`
        background: ${colors.neutral};
        border-color: ${colors.neutral};
    `)}
`;

const Group = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${(props) => (isSoft(props) ? '12px' : '10px')};
    width: 100%;
`;

/*
 * Slot de 32×32 sem preenchimento. No modal ele espelha a caixa do `MethodIcon`
 * para que os rótulos dos três métodos comecem na mesma coluna — mas sem o fundo
 * lilás dele: as diretrizes de marca da Google e da Apple não permitem o logo
 * sobre cor de fundo arbitrária.
 */
const IconSlot = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    ${(props) => isSoft(props) && css`
        width: 32px;
        height: 32px;
    `}
`;

const GoogleContainer = styled.div`
    position: relative;
    width: 100%;
    cursor: pointer;

    /* O foco real vive no iframe da Google; o anel precisa ser desenhado aqui. */
    &:focus-within {
        ${focusRing};
        border-radius: ${(props) => (isSoft(props) ? '10px' : '50px')};
    }
`;

const Face = styled.div`
    ${surface};
    position: relative;
    z-index: 2;
    pointer-events: none;

    ${GoogleContainer}:hover & {
        ${hovered};
    }
`;

/*
 * O widget oficial fica por baixo, só para receber o clique. Ele é encoberto
 * pela `Face`, que é opaca — de propósito, e não com `opacity: 0`: o Google
 * Identity Services tem heurística anti-clickjacking que pode recusar o clique
 * num botão transparente. Encobrir é seguro, apagar não é.
 */
const GoogleSlot = styled.div`
    position: absolute;
    inset: 0;
    z-index: 1;
    overflow: hidden;

    & > div {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        min-width: 100%;
        min-height: 100%;
    }

    /*
     * NÃO usar crase aqui dentro: este comentário vive dentro de um template
     * literal, e uma crase fecharia a string no meio do CSS.
     *
     * O alvo do clique é este [role='button'], não o iframe que o GSI também
     * renderiza aqui dentro — aquele é só o canal de postMessage dele. O botão
     * do Google nasce com 40px de altura e não há prop para mudar isso, então
     * ele é esticado até cobrir a casca inteira. Sem isso a metade de baixo do
     * botão não clica, e nada denuncia na tela: a Face continua desenhada.
     *
     * Seletor pelo role, e não pelas classes (.nsm7Bb-HzV7m-LgbsSe), porque as
     * classes do GSI são ofuscadas e mudam sem aviso; o role é semântico.
     */
    & [role='button'] {
        height: ${({ shape }) => HEIGHT[shape] || HEIGHT.pill}px !important;
        max-height: none !important;
    }
`;

const AppleButton = styled.button`
    ${surface};
    cursor: pointer;

    &:hover:not(:disabled) {
        ${hovered};
    }

    &:active:not(:disabled) {
        transform: scale(0.99);
    }

    &:focus-visible {
        ${focusRing};
    }

    /* Sem opacity: ela derrubaria o rótulo abaixo de 4,5:1. O estado de espera é
       comunicado pelo spinner e pelo texto, não por transparência. */
    &:disabled {
        cursor: progress;
        color: ${colors.night};
    }
`;

const Spinner = styled.span`
    width: 18px;
    height: 18px;
    border: 2px solid ${colors.neutralDark};
    border-top-color: ${colors.night};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;

    @media (prefers-reduced-motion: reduce) {
        animation-duration: 2.4s;
    }
`;
